import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popover,
  Slide,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { styled } from '@material-ui/styles'
import ChatIcon from '@material-ui/icons/Chat'
import CloseIcon from '@material-ui/icons/Close'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic'
import OpenInFullIcon from '@material-ui/icons/OpenInFull'
import axios from 'axios'
import { DOMAIN, REST_API_END_POINT } from 'src/constants/DefaultValues'
import { createChatSocket } from 'src/Helpers/createSocket'
import ChatMessageListSignWise from './ChatLeftScreen/ChatMessageList'
import ChatMessageInputSignWise from './ChatLeftScreen/ChatMessageInputSignWise'
import profilePic from '../../../assets/defaultAvatar.png'
const avatar1 =
  'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770216224609_IMG_20251025_173926.jpg'
const avatar2 =
  'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770216281626_anoosha.jpg'
const avatar3 =
  'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770216459320_222.jpg'
const avatar4 =
  'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770358850242_helpdesk.jpg'
import { WA } from './whatsappTheme'
import ChatWidgetSettings from './ChatWidgetSettings'
import ChatIncomingToast, { notifyHelpDeskStatusChange, notifyIncomingChatMessage } from './ChatIncomingToast'
import { initChatNotificationSound, unlockChatNotificationSound } from './chatNotificationSound'
import ChatFeedbackPanel from './ChatFeedbackPanel'
import { buildReplyTo } from './chatReply'
import {
  downloadChatTranscript,
  getChatNotificationsEnabled,
  setChatNotificationsEnabled,
} from './chatWidgetPrefs'
import {
  END_CHAT_MESSAGE,
  getHelpDeskChatStarted,
  getCurrentSessionMessages,
  getLatestSessionMessages,
  hasActiveHelpDeskSession,
  hasEndedChat,
  hasPendingUserMessage,
  isEndMessage,
  loadDeliveredMessageIds,
  loadPendingFeedback,
  persistPendingFeedback,
  clearPendingFeedback,
  isFeedbackCompleted,
  markAndPersistSentDelivered,
  persistDeliveredMessageIds,
} from './helpDeskSession'

const HELP_DESK_USER_ID = 39 // support agent account (teams/get-user-active)

const STATIC_ADMIN_AGENTS = [
  {
    user_id: 16,
    employee: 'Anoosha V',
    team: 'Super Administrator',
    email: 'anoosha@skyniche.com',
    profile_pic:
      'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770216281626_anoosha.jpg',
  },
  {
    user_id: 28,
    employee: 'Albin Joy',
    team: 'Super Administrator',
    email: 'albin.joy@skyniche.com',
    profile_pic:
      'https://iesnxcfhhtlsfqnfpwkt.supabase.co/storage/v1/object/public/signwise-attachments/team-member-images/profile-pics/1770216224609_IMG_20251025_173926.jpg',
  },
  {
    user_id: 43,
    employee: 'Harsha M',
    team: 'Super Administrator',
    email: 'harsha@skyniche.com',
    profile_pic: null,
  },
]

const mergeMessageDeliveredState = (prev = [], next = [], agentOnline = false, userId) => {
  const stored = loadDeliveredMessageIds(userId)
  const prevById = new Map(prev.map((item) => [String(item.id), item]))
  const pendingLocal = prev.filter(
    (item) => String(item.id).startsWith('local-') && item.type === 'sent',
  )
  const deliveredTexts = new Set(
    prev.filter((item) => item.type === 'sent' && item.delivered === true).map((item) => item.text),
  )

  const merged = next.map((item) => {
    if (item.type !== 'sent') return item

    const existing = prevById.get(String(item.id))
    const localMatch = pendingLocal.find(
      (pending) => pending.text === item.text && pending.delivered != null,
    )
    const wasDelivered =
      agentOnline ||
      stored.has(String(item.id)) ||
      existing?.delivered === true ||
      localMatch?.delivered === true ||
      deliveredTexts.has(item.text)

    if (wasDelivered) {
      return { ...item, delivered: true }
    }

    if (existing?.delivered === false || localMatch?.delivered === false) {
      return { ...item, delivered: false }
    }

    return { ...item, delivered: false }
  })

  persistDeliveredMessageIds(
    userId,
    merged.filter((item) => item.type === 'sent' && item.delivered === true).map((item) => item.id),
  )
  return merged
}

const isHelpDeskGroup = (chat) =>
  chat?.conversation_type === 'group' &&
  (chat.participants || []).some(
    (participant) => Number(participant.user_id) === HELP_DESK_USER_ID,
  )

const isEndedGroup = (chat) =>
  chat?.last_message_type === 'end' ||
  String(chat?.last_message || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim() === 'Chat ended'

const SIGNWISE_TOPICS = [
  'production control board',
  'accepted routes',
  'customer orders',
  'clients',
  'projects',
  'design',
  'estimate',
  'sales order',
  'split order',
  'production',
  'installations',
  'inventory',
  'purchasing',
  'workflow',
  'staff management',
  'tax categories',
  'tickets',
]

function FourAvatars() {
  return (
    <Box sx={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
      <Avatar src={avatar1} sx={{ position: 'absolute', top: 0, left: 0, width: 28, height: 28, border: `2px solid ${WA.header}`, zIndex: 1 }} />
      <Avatar src={avatar2} sx={{ position: 'absolute', top: 0, right: 0, width: 28, height: 28, border: `2px solid ${WA.header}`, zIndex: 2 }} />
      <Avatar src={avatar3} sx={{ position: 'absolute', bottom: 0, left: 0, width: 28, height: 28, border: `2px solid ${WA.header}`, zIndex: 3 }} />
      <Avatar src={avatar4} sx={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, border: `2px solid ${WA.header}`, zIndex: 4 }} />
    </Box>
  )
}

const FabWrap = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1250,
  display: 'flex',
  alignItems: 'center',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  '&.is-expanded': {
    background: '#ffffff',
    padding: '6px 8px 6px 18px',
    borderRadius: 36,
    boxShadow: '0 12px 36px -4px rgba(2, 125, 194, 0.22), 0 6px 20px rgba(0, 0, 0, 0.09)',
    border: '1.5px solid rgba(2, 125, 194, 0.22)',
    '&.is-login-entering': {
      animation: 'cta-login-entrance 0.65s cubic-bezier(0.16, 1, 0.3, 1) both',
      transition: 'none !important',
      willChange: 'transform, opacity',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 16px 42px -4px rgba(2, 125, 194, 0.32), 0 8px 24px rgba(0, 0, 0, 0.12)',
    },
    [theme.breakpoints.down('xs')]: {
      bottom: 16,
      right: 16,
      padding: '4px 6px 4px 12px',
    },
  },
  '&.is-minimized': {
    background: 'transparent',
    padding: 0,
    borderRadius: '50%',
    boxShadow: 'none',
    border: 'none',
    '&:hover .help-desk-hover-card, & .help-desk-hover-card:hover': {
      opacity: 1,
      visibility: 'visible',
      transform: 'translateX(0)',
      pointerEvents: 'auto',
    },
    '&:hover .help-desk-bubble:not(.is-open) .help-desk-chat-icon': {
      opacity: 0,
    },
    '&:hover .help-desk-bubble:not(.is-open) .help-desk-smile': {
      opacity: 1,
    },
  },
}))

const HoverCard = styled('div')({
  position: 'absolute',
  right: 72,
  whiteSpace: 'nowrap',
  backgroundColor: '#fff',
  color: '#2b2b2b',
  borderRadius: 8,
  padding: '10px 14px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateX(8px)',
  transition: 'opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease',
  pointerEvents: 'none',
})

const FabButton = styled('button')({
  position: 'relative',
  width: 'auto',
  height: 'auto',
  border: 'none',
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ChatBubble = styled('span')(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: WA.fab,
  color: '#fff',
  boxShadow: '0 6px 20px rgba(2, 125, 194, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'scale(1.06)',
    boxShadow: '0 8px 26px rgba(2, 125, 194, 0.55)',
  },
  '& .help-desk-chat-icon, & .help-desk-smile, & .MuiBadge-root': {
    position: 'relative',
    zIndex: 1,
  },
  '& .help-desk-chat-icon': {
    display: 'flex',
    opacity: 1,
    transition: 'opacity 0.18s ease',
  },
  '& .help-desk-smile': {
    position: 'absolute',
    top: '58%',
    left: '50%',
    transform: 'translate(-50%, -35%)',
    opacity: 0,
    transition: 'opacity 0.18s ease',
    pointerEvents: 'none',
  },
}))

const ChatButtonRect = styled('span')(({ theme }) => ({
  height: 44,
  borderRadius: 22,
  background: 'linear-gradient(135deg, #0284C7 0%, #015A8E 100%)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 20px',
  position: 'relative',
  overflow: 'hidden',
  fontWeight: 700,
  fontSize: 14.5,
  fontFamily: 'inherit',
  letterSpacing: '0.3px',
  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  transition: 'transform 0.2s ease, filter 0.2s ease',
  '&.is-login-entering': {
    animation: 'btn-login-shake 0.55s ease-in-out 0.35s 1 both',
    transition: 'none !important',
    willChange: 'transform',
  },
  '&.is-steady': {
    animation: 'eye-catchy-shake 5s infinite ease-in-out, pulse-glow 2.5s infinite ease-in-out',
  },
  '&:hover': {
    filter: 'brightness(1.08)',
    transform: 'scale(1.02)',
  },
  '& .MuiBadge-root': {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(30%, -30%)',
    zIndex: 2,
  },
}))

const ChatPopupAnchor = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 92,
  right: 24,
  zIndex: 1240,
  width: 420,
  maxWidth: 'calc(100vw - 32px)',
  [theme.breakpoints.down('sm')]: {
    right: 8,
    bottom: 84,
    width: 'calc(100vw - 16px)',
  },
  '&.is-maximized': {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    zIndex: 1400,
  },
}))

const ChatPopup = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 560,
  maxHeight: 'calc(100vh - 130px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 20,
  border: `1px solid ${WA.border}`,
  boxShadow: WA.shadow,
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100vh - 110px)',
  },
  '&.is-maximized': {
    height: '100%',
    maxHeight: 'none',
    borderRadius: 0,
  },
}))

const buildHelpDeskContact = (agent) => ({
  id: HELP_DESK_USER_ID,
  user_id: HELP_DESK_USER_ID,
  conversation_id: null,
  new_conversation_id: `user_${HELP_DESK_USER_ID}`,
  conversation_type: 'single',
  first_name: agent?.first_name || 'Help',
  last_name: agent?.last_name || 'Desk',
  full_name:
    [agent?.first_name, agent?.last_name].filter(Boolean).join(' ').trim() ||
    'Help Desk',
  profile_pic: agent?.profile_pic || null,
  chat_status: agent?.online === 1 ? 1 : 4,
  participants: [],
})

export default function HelpDeskChatButton() {
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [loginEntrance, setLoginEntrance] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoginEntrance(false)
    }, 2200)
    return () => clearTimeout(timer)
  }, [])

  const [agent, setAgent] = useState(null)
  const [agentOnline, setAgentOnline] = useState(false)
  const [activeAgents, setActiveAgents] = useState([])
  const [agentsPopoverAnchorEl, setAgentsPopoverAnchorEl] = useState(null)
  const [unread, setUnread] = useState(0)
  const [messages, setMessages] = useState([])
  const [message, setMessage] = useState('')
  const [image, setImage] = useState([])
  const [chat, setChat] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [typingUser, setTypingUser] = useState('')
  const [refreshToken, setRefreshToken] = useState(false)
  const [historyReady, setHistoryReady] = useState(false)
  const [showLanding, setShowLanding] = useState(true)
  const topics = SIGNWISE_TOPICS
  const fetchMessagesRef = useRef(null)
  const markedReadIdsRef = useRef(new Set())
  const fetchUnreadRef = useRef(null)
  const messagesFingerprintRef = useRef('')
  const openRef = useRef(open)
  const agentOnlineRef = useRef(agentOnline)
  const socketRef = useRef(null)
  const endingChatRef = useRef(false)
  const forceLandingRef = useRef(false)
  const [endingChat, setEndingChat] = useState(false)
  const [confirmEndOpen, setConfirmEndOpen] = useState(false)
  const [leftWhileOffline, setLeftWhileOffline] = useState(false)
  const [seenOnlineWaiting, setSeenOnlineWaiting] = useState(false)
  const [helpDeskStatusNotices, setHelpDeskStatusNotices] = useState([])
  const prevAgentOnlineRef = useRef(agentOnline)
  const statusTrackingReadyRef = useRef(false)
  const [thread, setThread] = useState({ type: 'single' })
  const [hdGroups, setHdGroups] = useState([])
  const [leavingChat, setLeavingChat] = useState(false)
  const [joiningChat, setJoiningChat] = useState(false)
  const [peopleAnchorEl, setPeopleAnchorEl] = useState(null)
  const threadInitializedRef = useRef(false)
  const preferSingleAfterEndRef = useRef(false)
  const loginUnreadNotifiedRef = useRef(false)
  const fetchActiveAgentsRef = useRef(null)
  const [maximized, setMaximized] = useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = useState(() =>
    getChatNotificationsEnabled(),
  )
  const notificationsRef = useRef(notificationsEnabled)
  notificationsRef.current = notificationsEnabled
  const [feedbackOpen, setFeedbackOpen] = useState(() => {
    try {
      const raw = localStorage.getItem('user')
      if (!raw || raw === 'null') return false
      const parsed = JSON.parse(raw)
      return Boolean(loadPendingFeedback(parsed?.user_id))
    } catch {
      return false
    }
  })
  const [feedbackMeta, setFeedbackMeta] = useState(() => {
    try {
      const raw = localStorage.getItem('user')
      if (!raw || raw === 'null') return null
      const parsed = JSON.parse(raw)
      return loadPendingFeedback(parsed?.user_id) || null
    } catch {
      return null
    }
  })
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedbackSelected, setFeedbackSelected] = useState(null)
  const [replyTo, setReplyTo] = useState(null)

  const user = (() => {
    try {
      const raw = localStorage.getItem('user')
      if (!raw || raw === 'null' || raw === 'undefined') return null
      return JSON.parse(raw)
    } catch (e) {
      return null
    }
  })()

  const contact = buildHelpDeskContact(agent)
  const isGroupThread = thread?.type === 'group'
  const groupSessionEnded =
    isGroupThread &&
    hasEndedChat(messages) &&
    !hasActiveHelpDeskSession(messages)
  const useGroupThread = isGroupThread && !groupSessionEnded
  const conversationId = useGroupThread
    ? thread.conversation_id
    : HELP_DESK_USER_ID
  const conversationIdForEvent = useGroupThread
    ? thread.new_conversation_id || `chat_${thread.conversation_id}`
    : `user_${HELP_DESK_USER_ID}`
  const conversationType = useGroupThread ? 'group' : 'single'
  const conversationTypeValue = useGroupThread ? 2 : 1
  const threadParticipants = useGroupThread ? thread.participants || [] : []
  const myMembership = threadParticipants.find(
    (person) => Number(person.user_id) === Number(user?.user_id),
  )
  const needsJoin = useGroupThread && Number(myMembership?.join_status) === 0
  const getMemberName = (person) =>
    person?.full_name ||
    [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim() ||
    'User'
  const showGroupAvatars = isGroupThread && threadParticipants.length > 1
  const extraCount = Math.max(0, threadParticipants.length - 2)
  const isOriginalCustomer =
    !isGroupThread || Number(thread.created_by) === Number(user?.user_id)
  const canLeave = Boolean(isGroupThread && user?.user_id && !isOriginalCustomer)
  const threadRef = useRef(thread)
  const syncHdThreadsRef = useRef(null)
  const userIdRef = useRef(Number(user?.user_id) || 0)
  userIdRef.current = Number(user?.user_id) || 0

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    agentOnlineRef.current = agentOnline
  }, [agentOnline])

  useEffect(() => {
    threadRef.current = thread
  }, [thread])

  const openFeedbackPrompt = useCallback((meta) => {
    if (!meta?.conversationId || !user?.user_id) return
    persistPendingFeedback(user.user_id, meta)
    preferSingleAfterEndRef.current = true
    threadInitializedRef.current = true
    forceLandingRef.current = true
    setFeedbackMeta(meta)
    setFeedbackSelected(null)
    setFeedbackSubmitted(false)
    setFeedbackOpen(true)
    setShowLanding(true)
  }, [user?.user_id])

  const restoreFeedbackIfNeeded = useCallback(
    (chatRating) => {
      if (!user?.user_id) return
      if (isFeedbackCompleted(chatRating)) {
        clearPendingFeedback(user.user_id)
        setFeedbackOpen(false)
        setFeedbackMeta(null)
        setFeedbackSelected(null)
        setFeedbackSubmitted(false)
        setFeedbackSubmitting(false)
        forceLandingRef.current = true
        preferSingleAfterEndRef.current = true
        setShowLanding(true)
        return
      }
      const stored = loadPendingFeedback(user.user_id)
      if (!stored) return

      openFeedbackPrompt(stored)
    },
    [user?.user_id, openFeedbackPrompt],
  )

  useEffect(() => {
    if (!user?.user_id) return
    const pending = loadPendingFeedback(user.user_id)
    if (pending) {
      forceLandingRef.current = true
      preferSingleAfterEndRef.current = true
      setFeedbackMeta(pending)
      setFeedbackOpen(true)
      setShowLanding(true)
    }
  }, [user?.user_id])

  const fetchAgent = useCallback(async () => {
    try {
      const response = await axios.post(`${REST_API_END_POINT}teams/get-user-active`)
      if (response.data.status === 1) {
        const data = response.data.data
        setAgent(data)
        setAgentOnline(data?.online === 1)
      }
    } catch (error) {
      console.log('Error fetching help desk agent', error)
    }
  }, [])

  const fetchActiveAgents = useCallback(async () => {
    try {
      const today = new Date()
      // format YYYY-MM-DD
      const dateStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0')
      const response = await axios.post(`${REST_API_END_POINT}hawkowl/who-is-working`, {
        date: dateStr,
      })
      if (response.data.status === 1) {
        const workingAgents = (response.data.rows || []).filter((row) => !row.isLoggedOut)
        if (workingAgents.length > 0) {
          // Put the 3 static admins first in the list
          const staticAdminIds = new Set(STATIC_ADMIN_AGENTS.map((a) => Number(a.user_id)))
          const staticAdminEmails = new Set(
            STATIC_ADMIN_AGENTS.map((a) => (a.email || '').toLowerCase().trim())
          )

          // Other working agents who are not in the static admin list
          const otherWorkingAgents = workingAgents.filter(
            (ag) =>
              !staticAdminIds.has(Number(ag.user_id || ag.id)) &&
              !staticAdminEmails.has((ag.email || '').toLowerCase().trim())
          )

          // Static admins: merge with live session if logged in through normal flow
          const adminList = STATIC_ADMIN_AGENTS.map((staticAdmin) => {
            const loggedInMatch = workingAgents.find(
              (ag) =>
                Number(ag.user_id || ag.id) === Number(staticAdmin.user_id) ||
                (ag.email && ag.email.toLowerCase().trim() === staticAdmin.email.toLowerCase().trim())
            )
            return loggedInMatch
              ? { ...staticAdmin, ...loggedInMatch, team: staticAdmin.team || loggedInMatch.team }
              : staticAdmin
          })

          // Static admins first, followed by other online agents
          setActiveAgents([...adminList, ...otherWorkingAgents])
        } else {
          setActiveAgents([])
        }
      }
    } catch (error) {
      console.log('Error fetching active agents', error)
    }
  }, [])

  const syncHdThreads = useCallback(async (opts = {}) => {
    if (!user?.user_id) return
    try {
      const response = await axios.post(`${REST_API_END_POINT}chat/get-chat-list`, {
        usersId: user.user_id,
      })
      const chats = response.data?.data || []
      const groups = chats
        .filter(isHelpDeskGroup)
        .sort(
          (a, b) =>
            Number(b.last_message_time || 0) - Number(a.last_message_time || 0),
        )
      const activeGroups = groups.filter((group) => !isEndedGroup(group))
      setHdGroups(activeGroups)
      const selectGroupId = opts.selectGroupId
      const fallbackGroup = opts.fallbackGroup || null
      setThread((prev) => {
        if (selectGroupId) {
          preferSingleAfterEndRef.current = false
          threadInitializedRef.current = true
          const match = activeGroups.find(
            (group) => Number(group.conversation_id) === Number(selectGroupId),
          )
          if (match) {
            forceLandingRef.current = false
            setShowLanding(false)
            return { type: 'group', ...match }
          }
          // Don't reset to a new single chat if the group isn't in the list yet
          // (common right after Help Desk adds members).
          if (
            prev.type === 'group' &&
            Number(prev.conversation_id) === Number(selectGroupId)
          ) {
            forceLandingRef.current = false
            setShowLanding(false)
            if (fallbackGroup?.participants) {
              return {
                ...prev,
                participants: fallbackGroup.participants,
                group_name: fallbackGroup.group_name || prev.group_name,
              }
            }
            return prev
          }
          if (fallbackGroup) {
            forceLandingRef.current = false
            setShowLanding(false)
            return {
              type: 'group',
              conversation_id: Number(selectGroupId),
              new_conversation_id: `chat_${selectGroupId}`,
              conversation_type: 'group',
              group_name: fallbackGroup.group_name || 'Help Desk chat',
              created_by: fallbackGroup.created_by || user.user_id,
              participants: fallbackGroup.participants || [],
            }
          }
          if (prev.type === 'group') {
            forceLandingRef.current = false
            setShowLanding(false)
            return prev
          }
          return prev
        }
        if (preferSingleAfterEndRef.current) {
          threadInitializedRef.current = true
          return { type: 'single' }
        }
        if (prev.type === 'group') {
          const still = activeGroups.find(
            (group) => Number(group.conversation_id) === Number(prev.conversation_id),
          )
          threadInitializedRef.current = true
          // Keep the optimistic group while the chat list catches up after add-member
          if (!still) return prev
          return { type: 'group', ...still }
        }
        // Always adopt an active help-desk group when available — including when the
        // user was added while already logged in (first sync had locked onto single).
        threadInitializedRef.current = true
        const own = activeGroups.find(
          (group) => Number(group.created_by) === Number(user.user_id),
        )
        if (own) {
          forceLandingRef.current = false
          setShowLanding(false)
          return { type: 'group', ...own }
        }
        if (activeGroups[0]) {
          forceLandingRef.current = false
          setShowLanding(false)
          return { type: 'group', ...activeGroups[0] }
        }
        return prev
      })
      if (!openRef.current) {
        let count = 0
        chats.forEach((chat) => {
          if (Number(chat.user_id) === HELP_DESK_USER_ID) {
            if (!isEndedGroup(chat)) {
              count += Number(chat.unread_messages) || 0
            }
            return
          }
          if (isHelpDeskGroup(chat) && !isEndedGroup(chat)) {
            count += Number(chat.unread_messages) || 0
          }
        })
        setUnread(count)
        if (count > 0 && !loginUnreadNotifiedRef.current && notificationsRef.current) {
          loginUnreadNotifiedRef.current = true
          notifyIncomingChatMessage(
            {
              conversationId: 'helpdesk-unread',
              message: 'You were added to a Help Desk chat',
              contentType: 'text',
              senderId: HELP_DESK_USER_ID,
              sender: { first_name: 'Help', last_name: 'Desk' },
            },
            { myUserId: user.user_id, fallbackTitle: 'Help Desk' },
          )
        }
      }
    } catch (error) {
      console.log('Error fetching help desk threads', error)
    }
  }, [user?.user_id])

  syncHdThreadsRef.current = syncHdThreads

  const fetchUnread = useCallback(async () => {
    if (!user?.user_id || openRef.current) return
    await syncHdThreads()
  }, [user?.user_id, syncHdThreads])

  const fetchMessages = useCallback(async () => {
    if (!user?.user_id) return
    try {
      const response = await axios.post(`${REST_API_END_POINT}chat/fetch-messages`, {
        conversationId,
        userId: user.user_id,
        conversationType,
        chatOpenTime: null,
        selectedChatLogId: null,
      })
      if (response.data.status === 1) {
        const messageData = response.data.messageResult || []
        const fingerprint = messageData
          .map((item) => `${item.id}:${item.read_status}:${item.message}`)
          .join('|')
        if (fingerprint === messagesFingerprintRef.current) {
          return
        }
        messagesFingerprintRef.current = fingerprint
        const messageArray = messageData.map((item) => ({
          id: item.id,
          sender: item?.sender_id === user.user_id ? 'You' : item.full_name || item.first_name || 'Help Desk',
          text: item?.message || '',
          type: item?.sender_id === user.user_id ? 'sent' : 'received',
          profile_pic: item?.profile_pic || null,
          attachment: item?.attachment_url || null,
          contentType: item?.message_type || 'text',
          chat_log_id: item?.chat_log_id || null,
          created_at: item?.created_at || 'Invalid Date',
          read_status: item?.read_status ?? null,
        }))
        if (endingChatRef.current) {
          return
        }
        // After end chat we force the landing screen, but once a new session is active
        // (customer messaged again / agent joined), always show the latest messages.
        const sessionActive = hasActiveHelpDeskSession(messageArray)
        if (sessionActive) {
          forceLandingRef.current = false
        }
        if (forceLandingRef.current && !sessionActive) {
          setShowLanding(true)
        }
        setMessages((prev) =>
          mergeMessageDeliveredState(prev, messageArray, agentOnlineRef.current, user.user_id),
        )
        setShowLanding(!sessionActive || forceLandingRef.current)
        if (!sessionActive) {
          setIsTyping(false)
          setTypingUser('')
        }

        restoreFeedbackIfNeeded(response.data.chatRating || null)

        const unreadMessages = messageData.filter(
          (item) =>
            item.sender_id !== user.user_id &&
            item.read_status === 1 &&
            item.message_type !== 'system' &&
            item.message_type !== 'end' &&
            !markedReadIdsRef.current.has(Number(item.id)),
        )
        if (unreadMessages.length > 0) {
          const unreadMessageIdsString = unreadMessages.map((item) => item.id).join(',')
          unreadMessages.forEach((item) => markedReadIdsRef.current.add(Number(item.id)))
          await axios.post(`${REST_API_END_POINT}chat/update-read-status`, {
            messageId: unreadMessageIdsString,
            userId: user.user_id,
            conversationId,
            conversationType,
          })
          if (!openRef.current) {
            syncHdThreadsRef.current?.()
          } else {
            setUnread(0)
          }
        }
      } else {
        messagesFingerprintRef.current = ''
        setMessages([])
      }
    } catch (error) {
      console.log('Error fetching help desk messages', error)
    }
  }, [user?.user_id, conversationId, conversationType, restoreFeedbackIfNeeded])

  fetchMessagesRef.current = fetchMessages
  fetchUnreadRef.current = fetchUnread
  fetchActiveAgentsRef.current = fetchActiveAgents

  useEffect(() => {
    fetchAgent()
    fetchActiveAgents()
    fetchUnread()
  }, [fetchAgent, fetchActiveAgents, fetchUnread])

  useEffect(() => {
    const timer = setInterval(() => {
      syncHdThreadsRef.current?.()
      if (!openRef.current) fetchUnreadRef.current?.()
      fetchActiveAgentsRef.current?.()
    }, 8000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!open) {
      setHistoryReady(false)
      setIsTyping(false)
      setTypingUser('')
      return undefined
    }
    setHistoryReady(false)
    messagesFingerprintRef.current = ''
    markedReadIdsRef.current = new Set()
    syncHdThreads()
    const pending = user?.user_id ? loadPendingFeedback(user.user_id) : null
    if (pending) {
      forceLandingRef.current = true
      preferSingleAfterEndRef.current = true
      setFeedbackMeta(pending)
      setFeedbackOpen(true)
      setShowLanding(true)
    } else {
      setShowLanding(!hasActiveHelpDeskSession(messages))
    }
    fetchMessages().finally(() => setHistoryReady(true))
    // Badge count comes from syncHdThreads — do not zero it when opening the widget
    return undefined
  }, [open, fetchMessages, refreshToken, conversationId, conversationType, user?.user_id])

  useEffect(() => {
    const onIncoming = (event) => {
      const msg = event.detail
      const senderId = Number(msg?.senderId)
      const myId = Number(userIdRef.current)
      if (!myId || myId === HELP_DESK_USER_ID) return
      const received = String(msg?.conversationId || '')
      const participantIds = (msg?.participants || [])
        .map((participant) => Number(participant.user_id ?? participant.id))
        .filter(Boolean)
      const current = threadRef.current
      const groupIdFromMsg = received.startsWith('chat_')
        ? Number(received.replace('chat_', ''))
        : 0
      const onThisGroup =
        Boolean(groupIdFromMsg) &&
        current?.type === 'group' &&
        Number(current.conversation_id) === groupIdFromMsg
      const inParticipants = participantIds.includes(myId)
      const isGroupMessage = Boolean(groupIdFromMsg)
      const isForMe = isGroupMessage
        ? inParticipants || onThisGroup
        : received === `user_${myId}` || inParticipants
      if (!isForMe) return
      if (msg?.contentType === 'chat_rating') return
      // New activity after an ended chat should leave the forced landing screen
      if (!isEndMessage({ contentType: msg?.contentType, text: msg?.message, message: msg?.message })) {
        preferSingleAfterEndRef.current = false
        forceLandingRef.current = false
        clearPendingFeedback(myId)
        setFeedbackOpen(false)
        setFeedbackMeta(null)
      }
      notifyIncomingChatMessage(msg, {
        myUserId: myId,
        fallbackTitle: 'Help Desk',
        whenChatOpen: openRef.current,
      })
      if (isEndMessage({ contentType: msg?.contentType, text: msg?.message, message: msg?.message })) {
        // Group end: every customer in the group gets their own feedback prompt.
        // Single chat: store rating against the customer's user id (agent looks up by that).
        const endConversationId = groupIdFromMsg || myId
        const endConversationType = groupIdFromMsg ? 2 : 1
        const endParticipants =
          (msg?.participants || []).length > 0
            ? msg.participants
            : current?.participants || [{ user_id: HELP_DESK_USER_ID }]
        setThread({ type: 'single' })
        setMessages([])
        messagesFingerprintRef.current = ''
        setIsTyping(false)
        setTypingUser('')
        openFeedbackPrompt({
          conversationId: endConversationId,
          conversationType: endConversationType,
          chatLogId: msg?.chatLogId || msg?.chat_log_id || null,
          participants: endParticipants,
        })
        setTimeout(() => fetchMessagesRef.current?.(), 500)
        return
      }
      if (received.startsWith('chat_') && (inParticipants || onThisGroup)) {
        const groupId = groupIdFromMsg
        if (groupId) {
          preferSingleAfterEndRef.current = false
          forceLandingRef.current = false
          setShowLanding(false)
          // Stay on / switch to this group immediately so "add member" doesn't flash as a new chat.
          setThread((prev) => {
            if (prev.type === 'group' && Number(prev.conversation_id) === groupId) {
              return {
                ...prev,
                participants: msg?.participants?.length
                  ? msg.participants
                  : prev.participants,
              }
            }
            return {
              type: 'group',
              conversation_id: groupId,
              new_conversation_id: received,
              conversation_type: 'group',
              group_name: prev.group_name || 'Help Desk chat',
              created_by: prev.created_by || userIdRef.current,
              participants: msg?.participants || prev.participants || [],
            }
          })
          syncHdThreadsRef.current?.({
            selectGroupId: groupId,
            fallbackGroup: {
              group_name: 'Help Desk chat',
              created_by: userIdRef.current,
              participants: msg?.participants || [],
            },
          })
        }
        if (/left the chat$/i.test(String(msg?.message || '').trim()) && senderId !== myId) {
          setThread((prev) => {
            if (prev.type !== 'group' || !prev.participants?.length) return prev
            return {
              ...prev,
              participants: prev.participants.filter(
                (person) => Number(person.user_id) !== senderId,
              ),
            }
          })
        }
      }
      if (openRef.current) {
        // Force a refetch so new socket messages are never skipped by fingerprint match
        messagesFingerprintRef.current = ''
        setTimeout(() => fetchMessagesRef.current?.(), 350)
      } else {
        setUnread((prev) => prev + 1)
        // Re-sync the group thread (plain unread sync used to wipe a just-joined group)
        if (groupIdFromMsg && (inParticipants || onThisGroup)) {
          setTimeout(() => {
            syncHdThreadsRef.current?.({
              selectGroupId: groupIdFromMsg,
              fallbackGroup: {
                group_name: 'Help Desk chat',
                participants: msg?.participants || [],
              },
            })
          }, 700)
        } else {
          setTimeout(() => fetchUnreadRef.current?.(), 700)
        }
      }
    }
    window.addEventListener('signwise-chat-incoming', onIncoming)
    return () => window.removeEventListener('signwise-chat-incoming', onIncoming)
  }, [])

  useEffect(() => {
    initChatNotificationSound()
  }, [])

  useEffect(() => {
    if (!user?.user_id) return undefined
    const socket = createChatSocket()
    socketRef.current = socket

    const onMessage = (msg) => {
      if (msg?.domain !== DOMAIN) return
      window.dispatchEvent(new CustomEvent('signwise-chat-incoming', { detail: msg }))
    }
    const onTyping = (data) => {
      if (data?.domain !== DOMAIN) return
      const senderId = Number(data?.senderId)
      const myId = Number(user?.user_id)
      if (!senderId || !myId || senderId === myId) return

      const received = String(data?.conversationId || '')
      const participants = Array.isArray(data?.participants) ? data.participants : []
      const participantIds = participants
        .map((p) => Number(p.user_id ?? p.id))
        .filter(Boolean)
      const current = threadRef.current
      const groupId = received.startsWith('chat_')
        ? Number(received.replace('chat_', ''))
        : 0
      const onThisGroup =
        Boolean(groupId) &&
        current?.type === 'group' &&
        Number(current.conversation_id) === groupId
      const inParticipants = participantIds.includes(myId)
      const isGroup = Boolean(groupId)

      let matches = false
      if (isGroup) {
        matches = inParticipants || onThisGroup
      } else {
        matches = received === `user_${myId}` || inParticipants
      }
      if (!matches) return
      setTypingUser(data.user || { user_id: senderId, first_name: 'Help Desk' })
      setIsTyping(true)
    }
    const onStopTyping = (data) => {
      if (data?.domain !== DOMAIN) return
      const senderId = Number(data?.senderId)
      const myId = Number(user?.user_id)
      if (!senderId || senderId === myId) return
      setIsTyping(false)
      setTypingUser('')
    }
    const onHelpdeskOnline = (payload) => {
      if (Number(payload?.userId) !== HELP_DESK_USER_ID) return
      const online = Number(payload?.online) === 1
      setMessages((prev) => markAndPersistSentDelivered(user.user_id, prev, { forceAllSent: true }))
      setAgentOnline(online)
    }
    const onMessageRead = (payload) => {
      const ids = new Set((payload?.messageIds || []).map((id) => Number(id)))
      if (!ids.size) return
      if (threadRef.current?.type === 'group') {
        const readBy = Number(payload?.readByUserId)
        const myId = Number(user?.user_id)
        if (readBy && readBy !== myId) {
          fetchMessagesRef.current?.()
        }
        return
      }
      if (!agentOnlineRef.current) return
      setMessages((prev) =>
        prev.map((item) => (ids.has(Number(item.id)) ? { ...item, read_status: 0 } : item)),
      )
    }

    socket.on('message', onMessage)
    socket.on('typing', onTyping)
    socket.on('stop typing', onStopTyping)
    socket.on('helpdesk-online', onHelpdeskOnline)
    socket.on('message-read', onMessageRead)

    return () => {
      socket.off('message', onMessage)
      socket.off('typing', onTyping)
      socket.off('stop typing', onStopTyping)
      socket.off('helpdesk-online', onHelpdeskOnline)
      socket.off('message-read', onMessageRead)
    }
  }, [user?.user_id])

  const handleSendMessage = async (value) => {
    forceLandingRef.current = false
    setShowLanding(false)
    if (user?.user_id) clearPendingFeedback(user.user_id)
    setFeedbackOpen(false)
    setFeedbackMeta(null)
    const optimistic = {
      id: value?.messageId ? `local-${value.messageId}` : `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sender: 'You',
      text: value?.message || '',
      type: 'sent',
      profile_pic: null,
      attachment: value?.contentType === 'text' ? null : value?.attachment || null,
      contentType: value?.contentType || 'text',
      chat_log_id: messages[0]?.chat_log_id || null,
      created_at: Math.floor(Date.now() / 1000),
      read_status: 1,
      delivered: Boolean(agentOnline),
    }
    setMessages((prev) => [...prev, optimistic])
    try {
      preferSingleAfterEndRef.current = false
      await axios.post(`${REST_API_END_POINT}chat/send-message`, {
        chatLogId: useGroupThread ? messages[0]?.chat_log_id || null : null,
        conversation_id: conversationId,
        message: value?.message,
        sender_id: user.user_id,
        message_type: value?.contentType || 'text',
        attachment_url: value?.contentType === 'text' ? null : value?.attachment || null,
        userType: user.user_type,
        conversationType: conversationTypeValue,
      })
      socketRef.current?.emit('message', {
        conversationId: conversationIdForEvent,
        message: value?.message,
        sender: user,
        senderId: user.user_id,
        participants: threadParticipants,
        selectedChatLogId: null,
        domain: DOMAIN,
        contentType: value?.contentType || 'text',
      })
    } catch (error) {
      console.log('Error sending help desk message', error)
    }
    fetchMessages()
  }

  const handleTopicClick = (topic) => {
    handleSendMessage({
      message: `I need help with ${topic}`,
      contentType: 'text',
    })
  }

  const sessionMessages = getCurrentSessionMessages(messages)
  const showWelcome = showLanding || !hasActiveHelpDeskSession(messages)
  const agentJoined = getHelpDeskChatStarted(messages, { fromCustomerView: true })
  const hasUserMessage = hasPendingUserMessage(messages, 'sent')

  useEffect(() => {
    if (!agentJoined || showWelcome) {
      prevAgentOnlineRef.current = agentOnline
      statusTrackingReadyRef.current = false
      if (!agentJoined) setHelpDeskStatusNotices([])
      return
    }
    if (!statusTrackingReadyRef.current) {
      prevAgentOnlineRef.current = agentOnline
      statusTrackingReadyRef.current = true
      return
    }
    const wasOnline = prevAgentOnlineRef.current
    if (wasOnline && !agentOnline) {
      setHelpDeskStatusNotices((prev) => [
        ...prev,
        { id: `help-desk-offline-${Date.now()}`, type: 'offline', created_at: Math.floor(Date.now() / 1000) },
      ])
      notifyHelpDeskStatusChange({ type: 'offline', whenChatOpen: openRef.current })
    }
    if (!wasOnline && agentOnline) {
      setHelpDeskStatusNotices((prev) => [
        ...prev,
        { id: `help-desk-online-${Date.now()}`, type: 'online', created_at: Math.floor(Date.now() / 1000) },
      ])
      notifyHelpDeskStatusChange({ type: 'online', whenChatOpen: openRef.current })
    }
    prevAgentOnlineRef.current = agentOnline
  }, [agentOnline, agentJoined, showWelcome])

  useEffect(() => {
    if (!hasUserMessage) {
      setLeftWhileOffline(false)
      setSeenOnlineWaiting(false)
      return
    }
    // Keep these even after the agent joins so the intro/waiting messages stay visible
    if (!agentOnline) setLeftWhileOffline(true)
    if (agentOnline) setSeenOnlineWaiting(true)
  }, [hasUserMessage, agentOnline])

  useEffect(() => {
    if (!agentOnline || !user?.user_id) return
    setMessages((prev) => markAndPersistSentDelivered(user.user_id, prev, { forceAllSent: true }))
  }, [agentOnline, user?.user_id])

  const requestEndChat = (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!agentJoined || canLeave || endingChatRef.current) return
    setConfirmEndOpen(true)
  }

  const handleEndChat = async (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    setConfirmEndOpen(false)
    if (!agentJoined || canLeave || endingChatRef.current) return
    endingChatRef.current = true
    forceLandingRef.current = true
    preferSingleAfterEndRef.current = true
    threadInitializedRef.current = true
    setEndingChat(true)
    setShowLanding(true)
    setHelpDeskStatusNotices([])
    setIsTyping(false)
    setTypingUser('')
    const groupConversationId = conversationId
    const groupConversationType = conversationTypeValue
    const groupConversationIdForEvent = conversationIdForEvent
    const groupParticipants = threadParticipants
    const groupChatLogId = messages[0]?.chat_log_id || null
    try {
      await axios.post(`${REST_API_END_POINT}chat/send-message`, {
        chatLogId: groupChatLogId,
        conversation_id: groupConversationId,
        message: END_CHAT_MESSAGE,
        sender_id: user.user_id,
        message_type: 'end',
        attachment_url: null,
        userType: user.user_type,
        conversationType: groupConversationType,
      })
      socketRef.current?.emit('message', {
        conversationId: groupConversationIdForEvent,
        message: END_CHAT_MESSAGE,
        sender: user,
        senderId: user.user_id,
        participants: groupParticipants,
        selectedChatLogId: null,
        domain: DOMAIN,
        contentType: 'end',
        chatLogId: groupChatLogId,
      })
      setThread({ type: 'single' })
      messagesFingerprintRef.current = ''
      setMessages([])
      openFeedbackPrompt({
        conversationId: useGroupThread ? groupConversationId : user.user_id,
        conversationType: useGroupThread ? groupConversationType : 1,
        chatLogId: groupChatLogId,
        participants: groupParticipants,
      })
    } catch (error) {
      console.log('Error ending help desk chat', error)
      preferSingleAfterEndRef.current = false
      forceLandingRef.current = false
      setShowLanding(false)
    } finally {
      endingChatRef.current = false
      setEndingChat(false)
    }
  }

  const closeFeedbackPanel = () => {
    if (user?.user_id) clearPendingFeedback(user.user_id)
    setFeedbackOpen(false)
    setFeedbackMeta(null)
    setFeedbackSelected(null)
    setFeedbackSubmitted(false)
    setFeedbackSubmitting(false)
    forceLandingRef.current = true
    preferSingleAfterEndRef.current = true
    setShowLanding(true)
  }

  const dismissFeedback = async () => {
    const meta = feedbackMeta
    closeFeedbackPanel()
    // Skip review → send transcript email without feedback
    if (meta?.conversationId && user?.user_id) {
      try {
        await axios.post(`${REST_API_END_POINT}chat/submit-rating`, {
          conversationId: meta.conversationId,
          conversationType: meta.conversationType,
          userId: user.user_id,
          rating: 'skip',
          chatLogId: meta.chatLogId,
        })
        socketRef.current?.emit('message', {
          conversationId:
            Number(meta.conversationType) === 2
              ? `chat_${meta.conversationId}`
              : `user_${user.user_id}`,
          message: 'Customer skipped review',
          sender: user,
          senderId: user.user_id,
          participants: (meta.participants || []).length
            ? meta.participants
            : [{ user_id: HELP_DESK_USER_ID }],
          selectedChatLogId: null,
          domain: DOMAIN,
          contentType: 'chat_rating',
          rating: 'skip',
        })
      } catch (error) {
        console.log('Error sending chat history after skip', error)
      }
    }
  }

  const handleFeedbackSelect = async (value) => {
    if (!user?.user_id || !feedbackMeta?.conversationId || feedbackSubmitting) return
    setFeedbackSelected(value)
    setFeedbackSubmitting(true)
    try {
      await axios.post(`${REST_API_END_POINT}chat/submit-rating`, {
        conversationId: feedbackMeta.conversationId,
        conversationType: feedbackMeta.conversationType,
        userId: user.user_id,
        rating: value,
        chatLogId: feedbackMeta.chatLogId,
      })
      clearPendingFeedback(user.user_id)
      socketRef.current?.emit('message', {
        conversationId:
          Number(feedbackMeta.conversationType) === 2
            ? `chat_${feedbackMeta.conversationId}`
            : `user_${user.user_id}`,
        message: value === 'up' ? 'Customer rated helpful' : 'Customer rated not helpful',
        sender: user,
        senderId: user.user_id,
        participants: (feedbackMeta.participants || []).length
          ? feedbackMeta.participants
          : [{ user_id: HELP_DESK_USER_ID }],
        selectedChatLogId: null,
        domain: DOMAIN,
        contentType: 'chat_rating',
        rating: value,
      })
      setFeedbackSubmitted(true)
      setTimeout(() => closeFeedbackPanel(), 1400)
    } catch (error) {
      console.log('Error submitting chat feedback', error)
      setFeedbackSelected(null)
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  const handleJoinChat = async () => {
    if (!needsJoin || joiningChat || !thread?.conversation_id || !user?.user_id) return
    setJoiningChat(true)
    try {
      const response = await axios.post(`${REST_API_END_POINT}chat/join-helpdesk-chat`, {
        conversationId: thread.conversation_id,
        userId: user.user_id,
      })
      const res = response?.data
      if (res?.status === 1) {
        if (res.participants?.length) {
          setThread((prev) => ({
            ...prev,
            participants: res.participants,
          }))
        }
        if (res.joinMessage) {
          socketRef.current?.emit('message', {
            conversationId: conversationIdForEvent,
            message: res.joinMessage,
            sender: user,
            senderId: user.user_id,
            participants: res.participants || threadParticipants,
            selectedChatLogId: null,
            domain: DOMAIN,
            contentType: 'system',
          })
        }
        messagesFingerprintRef.current = ''
        await syncHdThreads()
        await fetchMessages()
      }
    } catch (error) {
      console.log('Error joining help desk chat', error)
    } finally {
      setJoiningChat(false)
    }
  }

  const handleLeaveChat = async (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!canLeave || leavingChat || !thread?.conversation_id) return
    setLeavingChat(true)
    try {
      await axios.post(`${REST_API_END_POINT}chat/leave-helpdesk-chat`, {
        conversationId: thread.conversation_id,
        userId: user.user_id,
      })
      socketRef.current?.emit('message', {
        conversationId: conversationIdForEvent,
        message: `${user?.first_name || 'A user'} left the chat`,
        sender: user,
        senderId: user.user_id,
        participants: (threadParticipants || []).filter(
          (person) => Number(person.user_id) !== Number(user.user_id),
        ),
        selectedChatLogId: null,
        domain: DOMAIN,
        contentType: 'system',
      })
      setThread({ type: 'single' })
      setShowLanding(true)
      messagesFingerprintRef.current = ''
      await syncHdThreads()
    } catch (error) {
      console.log('Error leaving help desk chat', error)
    } finally {
      setLeavingChat(false)
    }
  }

  const togglePopup = () => {
    unlockChatNotificationSound()
    setOpen((prev) => {
      const next = !prev
      if (next) {
        fetchActiveAgents()
      }
      return next
    })
  }

  const handleToggleNotifications = (enabled) => {
    setChatNotificationsEnabled(enabled)
    setNotificationsEnabled(enabled)
  }

  const handleDownloadTranscript = () => {
    downloadChatTranscript({
      messages: getLatestSessionMessages(messages),
      title: isGroupThread
        ? thread.group_name || 'Help Desk chat transcript'
        : 'Help Desk chat transcript',
    })
  }

  const settingsMenu = (
    <ChatWidgetSettings
      tone="light"
      maximized={maximized}
      onToggleMaximize={() => setMaximized((prev) => !prev)}
      notificationsEnabled={notificationsEnabled}
      onToggleNotifications={handleToggleNotifications}
      onDownloadTranscript={handleDownloadTranscript}
    />
  )

  if (!user?.user_id || Number(user.user_id) === HELP_DESK_USER_ID) {
    return null
  }

  return (
    <>
      <style>
        {`
          @keyframes cta-login-entrance {
            0% {
              opacity: 0;
              transform: translate3d(0, 45px, 0) scale(0.92);
            }
            65% {
              opacity: 1;
              transform: translate3d(0, -5px, 0) scale(1.02);
            }
            85% {
              transform: translate3d(0, 2px, 0) scale(0.99);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
          }

          @keyframes login-sonar-pulse {
            0% {
              transform: translate3d(0, 0, 0) scale(0.98);
              opacity: 0.85;
            }
            100% {
              transform: translate3d(0, 0, 0) scale(1.15, 1.35);
              opacity: 0;
            }
          }

          .login-ripple-ring {
            position: absolute;
            inset: -2px;
            border-radius: 36px;
            border: 1.5px solid #0284c7;
            pointer-events: none;
            z-index: -1;
            opacity: 0;
            will-change: transform, opacity;
          }
          .login-ripple-1 {
            animation: login-sonar-pulse 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.25s 1 forwards;
          }
          .login-ripple-2 {
            animation: login-sonar-pulse 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.55s 1 forwards;
          }

          @keyframes login-card-flare-anim {
            0% {
              transform: translate3d(-100%, 0, 0) skewX(-20deg);
              opacity: 0;
            }
            25% {
              opacity: 0.85;
            }
            100% {
              transform: translate3d(220%, 0, 0) skewX(-20deg);
              opacity: 0;
            }
          }

          .login-card-flare {
            position: absolute;
            inset: 0;
            border-radius: 36px;
            overflow: hidden;
            pointer-events: none;
            z-index: 5;
          }
          .login-card-flare::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 50%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent);
            animation: login-card-flare-anim 0.85s ease-out 0.35s 1 forwards;
            will-change: transform, opacity;
          }

          @keyframes btn-login-shake {
            0%, 100% {
              transform: translate3d(0, 0, 0) scale(1) rotate(0deg);
            }
            25% {
              transform: translate3d(0, 0, 0) scale(1.06) rotate(-4deg);
            }
            50% {
              transform: translate3d(0, 0, 0) scale(1.06) rotate(4deg);
            }
            75% {
              transform: translate3d(0, 0, 0) scale(1.02) rotate(-2deg);
            }
          }

          @keyframes point-intro-bounce {
            0%, 100% {
              transform: translate3d(0, 0, 0);
            }
            50% {
              transform: translate3d(6px, 0, 0);
            }
          }

          @keyframes cta-text-intro {
            0% {
              opacity: 0;
              transform: translate3d(10px, 0, 0);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }

          @keyframes eye-catchy-shake {
            0%, 65%, 100% {
              transform: scale(1) rotate(0deg);
            }
            70% {
              transform: scale(1.04) rotate(-4deg);
            }
            74% {
              transform: scale(1.04) rotate(4deg);
            }
            78% {
              transform: scale(1.04) rotate(-3deg);
            }
            82% {
              transform: scale(1.02) rotate(2deg);
            }
            86% {
              transform: scale(1) rotate(0deg);
            }
          }

          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 4px 16px rgba(2, 125, 194, 0.42), 0 0 0 0 rgba(2, 125, 194, 0.45);
            }
            50% {
              box-shadow: 0 6px 24px rgba(2, 125, 194, 0.65), 0 0 0 7px rgba(2, 125, 194, 0);
            }
          }

          @keyframes pulse-dot {
            0% {
              box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.8);
            }
            70% {
              box-shadow: 0 0 0 7px rgba(16, 185, 129, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
            }
          }

          @keyframes shimmer-sweep {
            0% {
              transform: translateX(-150%) skewX(-20deg);
            }
            22%, 100% {
              transform: translateX(260%) skewX(-20deg);
            }
          }

          .chat-btn-shimmer {
            position: absolute;
            top: 0;
            left: 0;
            width: 50%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
            animation: shimmer-sweep 3.8s infinite ease-in-out;
            pointer-events: none;
          }

          @keyframes guide-hand-point {
            0%, 100% {
              transform: translateX(0);
            }
            50% {
              transform: translateX(6px);
            }
          }
        `}
      </style>
      <ChatIncomingToast
        onOpen={() => {
          unlockChatNotificationSound()
          setOpen(true)
        }}
      />
      {!open && !minimized && (
      <FabWrap className={loginEntrance ? 'is-expanded is-login-entering' : 'is-expanded'}>
        {loginEntrance && (
          <>
            <span className="login-ripple-ring login-ripple-1" />
            <span className="login-ripple-ring login-ripple-2" />
            <span className="login-card-flare" />
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 1.5,
            pl: 0.5,
            animation: 'cta-text-intro 0.5s ease-out 0.3s both',
          }}
        >
          {(agentOnline || activeAgents.length > 0) && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#10B981',
                mr: 1.5,
                flexShrink: 0,
                animation: 'pulse-dot 2s infinite',
              }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <Typography
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                fontSize: 13.5,
                lineHeight: 1.2,
                letterSpacing: '0.2px',
              }}
            >
              Need help?
            </Typography>
            <Typography
              sx={{
                color: '#475569',
                fontWeight: 500,
                fontSize: 11.5,
                lineHeight: 1.2,
                mt: '2px',
              }}
            >
              We're always here for you.
            </Typography>
          </Box>
          <Box
            sx={{
              fontSize: 20,
              ml: 1.5,
              animation: loginEntrance
                ? 'point-intro-bounce 0.6s 2 ease-in-out'
                : 'guide-hand-point 1.2s infinite ease-in-out',
              display: 'inline-flex',
            }}
          >
            👉
          </Box>
        </Box>
        <FabButton type="button" onClick={togglePopup} aria-label="Chat with a Live Person">
          <ChatButtonRect className={loginEntrance ? 'is-login-entering help-desk-bubble' : 'is-steady help-desk-bubble'}>
            <span className="chat-btn-shimmer" />
            <ChatIcon style={{ fontSize: 20, marginRight: 8 }} />
            Chat with Us
            <Badge
              badgeContent={unread}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  zIndex: 3,
                  minWidth: 18,
                  height: 18,
                  fontSize: 11,
                  fontWeight: 700,
                },
              }}
            />
          </ChatButtonRect>
        </FabButton>
        <Tooltip title="Minimize to chat icon">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setMinimized(true)
            }}
            aria-label="Minimize"
            sx={{
              ml: 0.8,
              p: 0.5,
              color: '#94a3b8',
              '&:hover': { color: '#0f172a', bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </FabWrap>
      )}

      {!open && minimized && (
      <FabWrap className="is-minimized">
        <HoverCard className="help-desk-hover-card">
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}
          >
            {agentOnline ? 'Help Desk is online' : 'Help Desk is offline'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4a4a4a', lineHeight: 1.4, mt: 0.5 }}>
            {agentOnline
              ? 'Chat with our support team.'
              : 'Leave a message — an agent will join when online.'}
          </Typography>
        </HoverCard>
        <Tooltip title="Expand to banner view">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setMinimized(false)
            }}
            aria-label="Expand banner"
            sx={{
              position: 'absolute',
              top: -6,
              left: -6,
              width: 24,
              height: 24,
              bgcolor: '#ffffff',
              color: '#0284C7',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              border: '1.5px solid #0284C7',
              zIndex: 10,
              transition: 'transform 0.2s ease',
              '&:hover': {
                bgcolor: '#f0f9ff',
                transform: 'scale(1.15)',
              },
            }}
          >
            <OpenInFullIcon style={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        <FabButton type="button" onClick={togglePopup} aria-label="Chat with Help Desk">
          <ChatBubble className="help-desk-bubble">
            <span className="help-desk-chat-icon">
              <ChatIcon style={{ fontSize: 28 }} />
            </span>
            <svg
              className="help-desk-smile"
              width="28"
              height="16"
              viewBox="0 0 28 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 4c4.5 8 15.5 8 20 0"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
              />
            </svg>
            <Badge
              badgeContent={unread}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  zIndex: 3,
                  minWidth: 18,
                  height: 18,
                  fontSize: 11,
                  fontWeight: 700,
                },
              }}
            />
          </ChatBubble>
        </FabButton>
      </FabWrap>
      )}

      <ChatPopupAnchor
        className={maximized ? 'is-maximized' : undefined}
        sx={{ pointerEvents: open ? 'auto' : 'none' }}
      >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <ChatPopup className={maximized ? 'is-maximized' : undefined}>
          {showWelcome ? (
            <Box
              sx={{
                px: 2.5,
                pt: 2.5,
                pb: 2.5,
                pr: 1.5,
                bgcolor: WA.header,
                color: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2 }}>
                  <FourAvatars />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 24,
                      lineHeight: 1.2,
                    }}
                  >
                    Hi there! 👋
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      opacity: 0.95,
                      mt: 0.4,
                      lineHeight: 1.2,
                    }}
                  >
                    Welcome to our L2 Support
                  </Typography>
                  <Typography
                    onClick={(e) => {
                      if (activeAgents.length > 0) {
                        setAgentsPopoverAnchorEl(e.currentTarget)
                      }
                    }}
                    sx={{
                      mt: 0.75,
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: 0.95,
                      cursor: activeAgents.length > 0 ? 'pointer' : 'default',
                      '&:hover': activeAgents.length > 0 ? { opacity: 1, textDecoration: 'underline' } : {},
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: activeAgents.length > 0 || agentOnline ? '#44b700' : '#bdbdbd',
                        mr: 1,
                        boxShadow: '0 0 0 2px rgba(255,255,255,0.2)',
                      }}
                    />
                    {activeAgents.length > 0 ? `${activeAgents.length} agents online` : (agentOnline ? 'We are online' : 'We are offline')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, ml: 0.5 }}>
                  {settingsMenu}
                  <IconButton
                    size="small"
                    onClick={togglePopup}
                    aria-label="Close chat"
                    sx={{
                      color: '#fff',
                      width: 36,
                      height: 36,
                      p: 0,
                      flexShrink: 0,
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Popover
                open={Boolean(agentsPopoverAnchorEl)}
                anchorEl={agentsPopoverAnchorEl}
                onClose={() => setAgentsPopoverAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 260,
                    maxHeight: 320,
                    borderRadius: '12px',
                    boxShadow: WA.shadow,
                    border: `1px solid ${WA.border}`,
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: WA.text }}>
                  Active Agents
                </Typography>
                <List dense disablePadding sx={{ py: 0.5 }}>
                  {activeAgents.map((ag) => (
                    <ListItem key={ag.user_id || ag.email || ag.employee}>
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          alt={ag.employee}
                          src={ag.profile_pic || profilePic}
                          sx={{ width: 32, height: 32 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={ag.employee}
                        secondary={ag.team}
                        primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Popover>
            </Box>
          ) : (
            <Box
              sx={{
                pl: 1,
                pr: 0.5,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                bgcolor: WA.header,
                color: '#fff',
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  forceLandingRef.current = true
                  setShowLanding(true)
                }}
                aria-label="Back to home"
                sx={{
                  color: '#fff',
                  width: 36,
                  height: 36,
                  mr: 0.5,
                  p: 0,
                  flexShrink: 0,
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              {showGroupAvatars ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
                  {threadParticipants
                    .slice(0, extraCount > 0 ? 2 : 3)
                    .map((person, index) => {
                      const name = getMemberName(person)
                      return (
                        <Tooltip key={person.user_id || person.id} title={name} arrow>
                          <Avatar
                            alt={name}
                            src={
                              typeof person?.profile_pic === 'string'
                                ? person.profile_pic
                                : profilePic
                            }
                            sx={{
                              width: 36,
                              height: 36,
                              ml: index === 0 ? 0 : -1.25,
                              border: '2px solid',
                              borderColor: WA.header,
                              zIndex: threadParticipants.length - index,
                            }}
                          />
                        </Tooltip>
                      )
                    })}
                  {extraCount > 0 && (
                    <Tooltip title={`${extraCount} more`} arrow>
                      <Box
                        onClick={(event) => setPeopleAnchorEl(event.currentTarget)}
                        sx={{
                          width: 36,
                          height: 36,
                          ml: -1.25,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,255,255,0.22)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `2px solid ${WA.header}`,
                          zIndex: 0,
                          cursor: 'pointer',
                        }}
                      >
                        +{extraCount}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ) : (
              <Box sx={{ position: 'relative', mr: 1.5 }}>
                <Avatar
                  alt="Help Desk"
                  src={contact.profile_pic || profilePic}
                  sx={{ width: 40, height: 40 }}
                />
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    bgcolor: agentOnline ? '#44b700' : '#bdbdbd',
                    boxShadow: agentOnline ? '0 0 0 2px #fff' : 'none',
                  }}
                />
              </Box>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {isGroupThread
                    ? thread.group_name || 'Help Desk chat'
                    : 'Chat with Help Desk'}
                </Typography>
                {showGroupAvatars ? (
                  <Typography
                    variant="caption"
                    component="button"
                    type="button"
                    onClick={(event) => setPeopleAnchorEl(event.currentTarget)}
                    sx={{
                      opacity: 0.9,
                      border: 0,
                      background: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      p: 0,
                      font: 'inherit',
                    }}
                  >
                    {threadParticipants.length} persons
                  </Typography>
                ) : (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {agentJoined
                    ? agentOnline
                      ? 'An agent has joined'
                      : 'An agent has joined — they may reply when available'
                    : hasUserMessage
                    ? agentOnline
                      ? 'An agent will join shortly'
                      : 'Agents are offline — they will join when online'
                    : agentOnline
                    ? 'An agent is online'
                    : 'Agents are offline — leave a message'}
                </Typography>
                )}
                <Popover
                  open={Boolean(peopleAnchorEl)}
                  anchorEl={peopleAnchorEl}
                  onClose={() => setPeopleAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      minWidth: 240,
                      maxHeight: 320,
                      borderRadius: '12px',
                      boxShadow: WA.shadow,
                      border: `1px solid ${WA.border}`,
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: WA.text }}>
                    People in this chat
                  </Typography>
                  <List dense disablePadding sx={{ py: 0.5 }}>
                    {threadParticipants.map((person) => {
                      const name = getMemberName(person)
                      return (
                        <ListItem key={person.user_id || person.id}>
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Avatar
                              alt={name}
                              src={
                                typeof person?.profile_pic === 'string'
                                  ? person.profile_pic
                                  : profilePic
                              }
                              sx={{ width: 32, height: 32 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={name}
                            primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                </Popover>
              </Box>
              {canLeave && (
                <Button
                  type="button"
                  size="small"
                  onClick={handleLeaveChat}
                  disabled={leavingChat}
                  sx={{
                    mr: 0.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.7)',
                    minWidth: 88,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {leavingChat ? 'Leaving...' : 'Leave chat'}
                </Button>
              )}
              {agentJoined && isOriginalCustomer && (
                <Button
                  type="button"
                  size="small"
                  onClick={requestEndChat}
                  disabled={endingChat}
                  sx={{
                    mr: 0.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.7)',
                    minWidth: 88,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {endingChat ? 'Ending...' : 'End Chat'}
                </Button>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, ml: 0.5 }}>
                {settingsMenu}
                <IconButton
                  size="small"
                  onClick={togglePopup}
                  aria-label="Close chat"
                  sx={{
                    color: '#fff',
                    width: 36,
                    height: 36,
                    p: 0,
                    flexShrink: 0,
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
          {hdGroups.length > 1 && !showWelcome && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                overflowX: 'auto',
                px: 2,
                py: 1,
                bgcolor: '#fff',
                borderBottom: `1px solid ${WA.border}`,
              }}
            >
              {hdGroups.map((group) => (
                <Chip
                  key={group.conversation_id}
                  size="small"
                  label={group.group_name || 'Help Desk chat'}
                  clickable
                  onClick={() => {
                    preferSingleAfterEndRef.current = false
                    setHistoryReady(false)
                    setThread({ type: 'group', ...group })
                  }}
                  sx={{
                    bgcolor:
                      isGroupThread &&
                      Number(thread.conversation_id) === Number(group.conversation_id)
                        ? 'rgba(2, 125, 194, 0.12)'
                        : '#F4F6F8',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          )}

          <Card
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              boxShadow: 'none',
              borderRadius: 0,
            }}
          >
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {!historyReady ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                  }}
                >
                  <CircularProgress size={28} sx={{ color: WA.header }} />
                </Box>
              ) : feedbackOpen ? (
                <ChatFeedbackPanel
                  variant="prompt"
                  submitting={feedbackSubmitting}
                  submitted={feedbackSubmitted}
                  selected={feedbackSelected}
                  onSelect={handleFeedbackSelect}
                  onSkip={dismissFeedback}
                />
              ) : showWelcome ? (
                <Box sx={{
                  px: 2,
                  py: 2,
                  bgcolor: '#fff',
                  flexGrow: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(0, 0, 0, 0.25)',
                  },
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {topics.map((topic) => (
                      <Chip
                        key={topic}
                        label={String(topic).toLowerCase()}
                        clickable
                        onClick={() => handleTopicClick(topic)}
                        sx={{
                          height: 32,
                          borderRadius: '16px',
                          bgcolor: '#F4F6F8',
                          color: '#454F5B',
                          fontWeight: 500,
                          fontSize: 13,
                          '& .MuiChip-label': { px: 1.75 },
                          '&:hover': {
                            bgcolor: 'rgba(2, 125, 194, 0.1)',
                            color: WA.header,
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Avatar src={contact.profile_pic || profilePic} sx={{ width: 32, height: 32, mr: 1.5 }} />
                    <Box sx={{ bgcolor: '#F4F6F8', p: 1.5, borderRadius: '0px 12px 12px 12px', color: '#1a1a1a', fontSize: 14, maxWidth: '85%' }}>
                      Hi👋 what brings you here today? we will be happy to assist you
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ChatMessageListSignWise
                  messages={sessionMessages}
                  setMessages={setMessages}
                  isTyping={isTyping}
                  typingUser={typingUser}
                  waitingForAgent={!agentJoined && hasUserMessage}
                  agentJoined={agentJoined}
                  waitingAgentName={contact.full_name || 'Help Desk'}
                  waitingAgentPic={contact.profile_pic || profilePic}
                  agentOnline={agentOnline}
                  keepOfflineWaiting={leftWhileOffline}
                  keepOnlineWaiting={seenOnlineWaiting || (hasUserMessage && agentOnline)}
                  helpDeskStatusNotices={helpDeskStatusNotices}
                  participants={threadParticipants}
                  recipientOnline={agentOnline}
                  onJoinChat={needsJoin ? handleJoinChat : null}
                  joiningChat={joiningChat}
                  isGroupChat={useGroupThread}
                  onReply={(msg) => {
                    const next = buildReplyTo(msg)
                    if (next) setReplyTo(next)
                  }}
                />
                </Box>
              )}
            </Box>
            {!feedbackOpen && !needsJoin ? (
            <Box sx={{ flexShrink: 0, overflow: 'visible' }}>
              <ChatMessageInputSignWise
                messages={messages}
                selectedChatLogId={null}
                setOnChatScreen={() => {}}
                fetchMessages={fetchMessages}
                conversationId={conversationIdForEvent}
                onSend={handleSendMessage}
                chat={chat}
                setChat={setChat}
                isTyping={isTyping}
                setIsTyping={setIsTyping}
                message={message}
                setMessage={setMessage}
                image={image}
                setImage={setImage}
                typingUser={typingUser}
                setTypingUser={setTypingUser}
                handleRefresh={() => setRefreshToken((prev) => !prev)}
                refreshChat={() => setRefreshToken((prev) => !prev)}
                setRenderChat={() => {}}
                renderChat={refreshToken}
                participants={threadParticipants}
                whatsapp
                welcomeLayout={showWelcome}
                placeholder={showWelcome ? 'What can we help you with?' : 'Type a message'}
                replyTo={replyTo}
                onClearReply={() => setReplyTo(null)}
                sx={{ minWidth: 0, maxWidth: '100%', width: '100%' }}
              />
            </Box>
            ) : null}
          </Card>
          {confirmEndOpen && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                bgcolor: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
              onClick={() => !endingChat && setConfirmEndOpen(false)}
            >
              <Box
                onClick={(event) => event.stopPropagation()}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '14px',
                  p: 2.5,
                  width: '100%',
                  maxWidth: 320,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: WA.text, mb: 1 }}>
                  End this chat?
                </Typography>
                <Typography variant="body2" sx={{ color: WA.meta, mb: 2.5 }}>
                  Are you sure you want to end this chat? You can start a new conversation anytime.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    onClick={() => setConfirmEndOpen(false)}
                    disabled={endingChat}
                    sx={{ textTransform: 'none', color: WA.meta, fontWeight: 600 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEndChat}
                    disabled={endingChat}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: WA.header,
                      '&:hover': { bgcolor: WA.headerDark },
                    }}
                  >
                    {endingChat ? 'Ending...' : 'End Chat'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </ChatPopup>
      </Slide>
      </ChatPopupAnchor>
    </>
  )
}
